import rentalTypes from 'enums/rentalTypes';
import storageSiteFacilities from 'enums/storageSiteFacilities';
import paymentRecurrences from 'enums/paymentRecurrences';
import { convertToNumber, formatNumber, replaceAll } from 'helpers/StringHelper';
import { flattenArray } from 'helpers/ArrayHelper';
import { getDefaultPaymentProviders } from 'logic/paymentLogic';
import { parseIsoDate, formatIsoDate } from 'helpers/DateHelper';
import storageGroupCategories from 'enums/storageGroupCategories';
import storageGroupStatuses from 'enums/storageGroupStatuses';
import storageStatuses from 'enums/storageStatuses';
import additionalServiceStatuses from 'enums/additionalServiceStatuses';
import contactTypes from 'enums/contactTypes';
import organizationTypes from 'enums/organizationTypes';
import vatModes from 'enums/vatModes';
import lockTypes from 'enums/lockTypes';
import documentTypes from 'enums/documentTypes';
import { getOpeningTimeSpanRules, createOpeningTimeSpanRulesFormData } from 'helpers/OpeningTimeSpanHelper';
import bookingTimeModes from 'enums/bookingTimeModes';
import tenantAvailabilities from 'enums/tenantAvailabilities';
import storageSiteBookingUpdateModes from 'enums/storageSiteBookingUpdateModes';
import { createFormDataForTimePeriodPrice } from 'logic/timePeriodPriceLogic';

const formatNumberOptions = { skipThousandSeparators: true, sanitizeMinusSign: true };

export const createFormDataForStorageSite = (storageSite, appContext) => {
    if(!storageSite) {
        return undefined;
    }

    const location = storageSite.location
        ? { lat: storageSite.location.latitude, lng: storageSite.location.longitude }
        : undefined;

    const optionToTax = getOptionToTax(storageSite);

    return {
        title: storageSite.title,
        stepValidities: [],
        steps: [
            // 0
            {
                // we need separate keys for separate categories
                status: storageSite.status,
                ...createKeyValueObject(
                    appContext.storageGroupCategories,
                    category => category.key,
                    category => ({ storageGroups: storageSite.storageGroups.filter(o => o.category === category.key).map(o => createFormDataForStorageGroup(o, storageSite, appContext)) })),
                storageType: storageSite.storageType,
                area: formatNumber(storageSite.area, appContext),
                optionToTax,
                showDeletedStorageGroups: false,
                lockConfigurationDialog: createFormDataForStorageGroupLockConfigurationDialog()
            },

            // 1
            {
                street: storageSite.address,
                postalCode: storageSite.postalCode,
                city: storageSite.city,
                propertyReference: storageSite.propertyReference,
                location,
                mapCenter: location ||
                    {
                        // TODO: use user's location
                        lat: 59.07412,
                        lng: 15.1066
                    },
                mapZoom: location ? 12 : 6
            },

            // 2
            {
                doorHeight: formatNumber(storageSite.doorHeight, appContext, formatNumberOptions),
                doorWidth: formatNumber(storageSite.doorWidth, appContext, formatNumberOptions),
                environment: filterFacilities(storageSite.facilities, appContext.facilityCategories.environment.map(o => storageSiteFacilities[o])).length > 0
                    ? (storageSite.facilities.find(o => o === storageSiteFacilities.indoor.key)
                        ? storageSiteFacilities.indoor.key
                        : storageSiteFacilities.outdoor.key)
                    : undefined,
                facilities: filterFacilities(storageSite.facilities, appContext.facilityCategories.general.map(o => storageSiteFacilities[o])),
                securities: filterFacilities(storageSite.facilities, appContext.facilityCategories.security.map(o => storageSiteFacilities[o])),
                availability: storageSite.guestAvailability,
                guestAvailabilityCustomDescription: storageSite.guestAvailabilityCustomDescription,
                goodsTypes: storageSite.goodsTypes,
                visitAllowance: storageSite.visitAllowance,
                letOutAllowance: storageSite.letOutAllowance,
                minimumBookingLength: storageSite.minimumBookingLength,
                additionalServices: storageSite.additionalServices
                    .filter(additionalService => additionalService.status !== additionalServiceStatuses.deleted.key)
                    .map(additionalService => ({
                        id: additionalService.id,
                        description: additionalService.description,
                        currency: additionalService.currency,
                        paymentRecurrence: additionalService.paymentRecurrence,
                        type: additionalService.type,
                        timePeriodPrices: additionalService.timePeriodPrices.map(tpp => createFormDataForTimePeriodPrice(tpp, additionalService.currency, appContext))
                    })),
                serviceDescription: storageSite.serviceDescription,
                usePriceAdjustment: !!storageSite.annualPriceAdjustmentDate,
                annualPriceAdjustmentDate: storageSite.annualPriceAdjustmentDate,
                annualPriceAdjustmentPercentage: storageSite.annualPriceAdjustmentPercentage
                    ? formatNumber(100 * storageSite.annualPriceAdjustmentPercentage, appContext, formatNumberOptions)
                    : undefined,
                allowedTimeModes: storageSite.allowedTimeModes,
                projectCode: storageSite.projectCode
            },

            // 3
            {
                useOpeningTimeSpanRules: storageSite.openingTimeSpanRules.length > 0 ? 'true' : 'false',
                openingTimeSpanRules: createOpeningTimeSpanRulesFormData(storageSite.openingTimeSpanRules),

                // 3b
                discounts: storageSite.discounts.map(o => createFormDataForDiscount(o, appContext))
            },

            // 4
            {
                title: storageSite.title,
                description: storageSite.description,
                descriptionGivenAfterPurchase: storageSite.descriptionGivenAfterPurchase,
                lockCode: storageSite.lockCode,
                attentionText: storageSite.attentionText,
                attentionTextVisibilityDeadline: storageSite.attentionTextVisibilityDeadline,
                images: storageSite.images.map(image => ({
                    id: image.id,
                    mediaUrl: image.mediaUrl
                })),
                videos: (storageSite.videos || []).map(video => ({
                    id: video.id,
                    mediaUrl: video.mediaUrl,
                    baseUrl: video.baseUrl
                })),
                documents: storageSite.documents.filter(document => document.type === documentTypes.document.key).map(createDocumentFormData),
                agreements: storageSite.documents.filter(document => document.type === documentTypes.agreement.key).map(createDocumentFormData),
            },

            // 5
            {
                commissionRate: storageSite.commissionRate === undefined
                    ? undefined
                    : formatNumber(100 * storageSite.commissionRate, appContext, formatNumberOptions),
                searchable: storageSite.searchable,
                searchPriority: storageSite.searchPriority,
                regionSearchPriority: storageSite.regionSearchPriority,
                distanceSearchAdjustment: formatNumber(-storageSite.distanceSearchAdjustment / 1000, appContext, formatNumberOptions),
                allowImmediateBookings: storageSite.allowImmediateBookings,
                considerBookingsAsServices: storageSite.considerBookingsAsServices,
                contacts: createFormDataForStorageSiteContacts(storageSite),
                lockType: storageSite.lockType ?? 'none',
                lockConfigurations: createFormDataForStorageSiteLockConfigurations(storageSite),
                paymentProviders: storageSite.paymentProviders,
                lockSystemUsers: createFormDataForStorageSiteLockSystemUsers(storageSite),
            }
        ],
        priceAffectedBookingsDialog: createFormDataForPriceAffectedBookingsDialog()
    };
};

export const createFormDataForNewStorageSite = appContext => {
    return {
        stepValidities: [],
        steps: [
            // 0
            {
                status: undefined,
                // we need sepearate keys for separate categories
                ...createKeyValueObject(appContext.storageGroupCategories, category => category.key, () => ({ storageGroups: [] })),
                storageType: undefined,
                area: undefined,
                optionToTax: false,
                lockConfigurationDialog: createFormDataForStorageGroupLockConfigurationDialog()
            },

            // 1
            {
                street: undefined,
                postalCode: undefined,
                city: undefined,
                propertyReference: undefined,
                location: {
                    lat: undefined,
                    lng: undefined
                },
                mapCenter: {
                    // TODO: use user's location
                    lat: 59.07412,
                    lng: 15.1066
                },
                mapZoom: 6
            },

            // 2
            {
                doorHeight: undefined,
                doorWidth: undefined,
                environment: undefined,
                facilities: [],
                securities: [],
                availability: undefined,
                guestAvailabilityCustomDescription: undefined,
                goodsTypes: [],
                visitAllowance: undefined,
                letOutAllowance: undefined,
                minimumBookingLength: 30,
                additionalServices: [],
                serviceDescription: undefined,
                usePriceAdjustment: false,
                annualPriceAdjustmentDate: undefined,
                annualPriceAdjustmentPercentage: undefined,
                allowedTimeModes: [bookingTimeModes.period.key, bookingTimeModes.subscription.key],
                projectCode: undefined
            },

            // 3
            {
                useOpeningTimeSpanRules: 'false',
                openingTimeSpanRules: [],

                // 3b
                discounts: []
            },

            // 4
            {
                title: undefined,
                description: undefined,
                descriptionGivenAfterPurchase: undefined,
                lockCode: undefined,
                attentionText: undefined,
                attentionTextVisibilityDeadline: undefined,
                images: [],
                videos: [],
                documents: [],
                agreements: []
            },

            // 5
            {
                commissionRate: undefined,
                searchable: true,
                searchPriority: 0,
                regionSearchPriority: 0,
                distanceSearchAdjustment: 0,
                allowImmediateBookings: false,
                considerBookingsAsServices: false,
                contacts: {
                    [contactTypes.caretakerEmail.key]: undefined,
                    [contactTypes.caretakerMobileTelephone.key]: undefined,
                    [contactTypes.caretakerName.key]: undefined,
                    [contactTypes.roundReportEmail.key]: undefined
                },
                lockType: 'none',
                lockConfiguration: undefined,
                paymentProviders: getDefaultPaymentProviders(appContext).map(o => o.key)
            }
        ],
        priceAffectedBookingsDialog: createFormDataForPriceAffectedBookingsDialog()
    };
};

export const createFormDataForNewStorageGroup = (category, allStorageGroups, defaultCommissionRate, appContext) => {
    const tpp = createFormDataForEmptyTimePeriodPrice(undefined, defaultCommissionRate, appContext);

    return {
        id: undefined,
        category: category.key,
        rentalType: category.defaultRentalType.key,
        storagesAreRectangular: true,
        title: undefined,
        storageLength: undefined,
        storageWidth: undefined,
        storageArea: undefined,
        ceilingHeight: undefined,
        currency: appContext.currency.code,
        quantity: undefined,
        startDate: new Date(),
        endDate: undefined,
        enumerateStorages: false,
        status: storageGroupStatuses.active.key,
        storages: [],
        storageIds: [],
        isDraft: true,
        editor: undefined,
        allowCustomArea: category.allowCustomArea && !getCustomAreaExists(allStorageGroups),
        timePeriodPrices: [tpp],
        lockConfigurations: []
    };
};

export const createFormDataForNewStorage = title => {
    return {
        id: undefined,
        title: title,
        startDate: new Date(),
        endDate: undefined,
        status: storageStatuses.active.key
    };
};

export const createFormDataForNewAdditionalService = (actor, defaultCommissionRate, appContext) => {
    const tpp = createFormDataForEmptyTimePeriodPrice(actor, defaultCommissionRate, appContext);

    return {
        id: undefined,
        description: undefined,
        currency: appContext.currency.code,
        paymentRecurrence: paymentRecurrences.oneTime.key,
        timePeriodPrices: [tpp],
        isDraft: true,
        editor: undefined
    };
};

export const createFormDataForNewLockConfiguration = () => {
    return {
        lockType: lockTypes.telkey.key,
        data: undefined,
        name: undefined
    };
};

export const createStorageSiteRequestFromFormData = (formValues, baseValues, actor, priceAffectedBookings, appContext) => {
    const [ step0, step1, step2, step3, step4, step5 ] = formValues.steps; // eslint-disable-line no-unused-vars
    const storageSite = {
        storageType: getStorageTypeFromFormData(formValues),

        // step 0
        status: step0.status,
        storageGroups: createStorageGroupsFromFormData(formValues, actor, appContext),
        area: getStorageSiteArea(formValues),
        optionToTax: getOptionToTaxFromFormData(formValues),

        // step 1
        address: step1.street,
        postalCode: step1.postalCode,
        city: step1.city,
        propertyReference: step1.propertyReference,
        location: step1.location.lat && step1.location.lng
            ? { latitude: step1.location.lat, longitude: step1.location.lng }
            : undefined,

        // step 2
        minimumBookingLength: convertToNumber(step2.minimumBookingLength),
        doorHeight: convertToNumber(step2.doorHeight),
        doorWidth: convertToNumber(step2.doorWidth),
        facilities: step2.facilities.concat(step2.securities).concat([step2.environment]).filter(o => o),
        guestAvailability: step2.availability,
        guestAvailabilityCustomDescription: step2.availability === tenantAvailabilities.customDescription.key
            ? step2.guestAvailabilityCustomDescription
            : undefined,
        goodsTypes: step2.goodsTypes,
        visitAllowance: step2.visitAllowance,
        letOutAllowance: step2.letOutAllowance,
        additionalServices: step2.additionalServices.map(o => createAdditionalServiceFromFormData(o, appContext)),
        serviceDescription: step2.serviceDescription,
        annualPriceAdjustmentDate: step2.usePriceAdjustment
            ? step2.annualPriceAdjustmentDate
            : undefined,
        annualPriceAdjustmentPercentage: step2.usePriceAdjustment
            ? convertToNumber(step2.annualPriceAdjustmentPercentage) / 100
            : undefined,
        allowedTimeModes: step2.allowedTimeModes,
        projectCode: step2.projectCode,

        // step 3
        openingTimeSpanRules: step3.useOpeningTimeSpanRules === 'true'
            ? getOpeningTimeSpanRules(step3.openingTimeSpanRules, appContext)
            : [],
        // 3b
        discounts: step3.discounts.map(discount => getDiscountFromFormData(discount, appContext)),

        // step 4
        title: step4.title,
        description: step4.description,
        descriptionGivenAfterPurchase: step4.descriptionGivenAfterPurchase,
        lockCode: step4.lockCode,
        attentionText: step4.attentionText,
        attentionTextVisibilityDeadline: step4.attentionTextVisibilityDeadline === null
            ? undefined
            : formatIsoDate(step4.attentionTextVisibilityDeadline, appContext),
        images: step4.images.map(o => ({
            id: o.id,
            mediaUrl: o.mediaUrl
        })),
        videos: step4.videos,
        documents: [
            ...step4.documents.map(o => ({ ...o, type: documentTypes.document.key })),
            ...step4.agreements.map(o => ({ ...o, type: documentTypes.agreement.key }))
        ],

        // step 5
        commissionRate: convertToNumber(step5.commissionRate) === undefined
            ? undefined
            : convertToNumber(step5.commissionRate) / 100,
        searchable: step5.searchable,
        searchPriority: convertToNumber(step5.searchPriority) || 0,
        regionSearchPriority: convertToNumber(step5.regionSearchPriority) || 0,
        distanceSearchAdjustment: -1000 * (convertToNumber(step5.distanceSearchAdjustment) || 0),
        allowImmediateBookings: step5.allowImmediateBookings,
        considerBookingsAsServices: step5.considerBookingsAsServices,
        contacts: flattenArray(Object.keys(step5.contacts).map(type => parseContact(type, step5.contacts[type]))),
        lockType: step5.lockType === 'none'
            ? undefined
            : step5.lockType,
        lockConfiguration: step5.lockType === 'none'
            ? undefined
            : step5.lockConfigurations[step5.lockType],
        lockSystemUsers: step5.lockSystemUsers?.map(o => ({
            id: o.id,
            name: o.name,
            description: o.description,
            lockSystemUserIdentifier: o.lockSystemUserIdentifier,
            startDate: formatIsoDate(o.startDate, appContext),
            endDate: formatIsoDate(o.endDate, appContext)
        })),
        paymentProviders: step5.paymentProviders,

        bookingUpdateSettings: {
            updateMode: formValues.priceAffectedBookingsDialog.updateMode,
            sendPriceChangeEmails: formValues.priceAffectedBookingsDialog.sendPriceChangeEmails,
            bookingIds: priceAffectedBookings
                ?.map((o, i) => formValues.priceAffectedBookingsDialog.bookings[i].selected ? o.existingBooking.id : undefined)
                .filter(o => o),
            priceChangeEmailCustomMessage: formValues.priceAffectedBookingsDialog.priceChangeEmailCustomMessage
        },

        ...baseValues // id?, status?
    };
    return storageSite;
};

export const getCustomAreaExists = storageGroups => storageGroups.find(sg => sg.rentalType === rentalTypes.customArea.key) !== undefined;

export const calculateTotalArea = storageGroups => {
    let area = 0;
    storageGroups.forEach(storageGroup => {
        switch(storageGroup.rentalType) {
            case rentalTypes.fixedArea.key:
                area = (area || 0) + (convertToNumber(storageGroup.quantity) || 0) * (convertToNumber(storageGroup.storageLength) || 0) * (convertToNumber(storageGroup.storageWidth) || 0);
                break;

            case rentalTypes.wholeSite.key:
                area = (area || 0) + (convertToNumber(storageGroup.storageLength) || 0) * (convertToNumber(storageGroup.storageWidth) || 0);
                break;

            default:
                break;
        }
    });
    return area;
};

export const getAllStorageGroups = formValues => [].concat.apply(
    [],
    Object.keys(storageGroupCategories).map(category => formValues.steps[0][category]?.storageGroups || [])
);

const createKeyValueObject = (items, keySelector, valueSelector) => {
    const result = {};
    items.forEach(item => {
        result[keySelector(item)] = valueSelector(item);
    });
    return result;
};

const createFormDataForStorageGroup = (storageGroup, storageSite, appContext) => {
    return {
        id: storageGroup.id,
        category: storageGroup.category,
        rentalType: storageGroup.rentalType,
        title: storageGroup.title,
        storageLength: formatNumber(storageGroup.storageLength, appContext, formatNumberOptions),
        storageWidth: formatNumber(storageGroup.storageWidth, appContext, formatNumberOptions),
        storageArea: formatNumber(storageGroup.storageArea, appContext, formatNumberOptions),
        ceilingHeight: formatNumber(storageGroup.ceilingHeight, appContext, formatNumberOptions),
        minBookableArea: formatNumber(storageGroup.minBookableArea, appContext, formatNumberOptions),
        timePeriodPrices: storageGroup.timePeriodPrices.map(tpp => createFormDataForTimePeriodPrice(tpp, storageGroup.currency, appContext)),
        currency: storageGroup.currency,
        status: storageGroup.status,
        enumerateStorages: storageGroup.enumerateStorages,
        startDate: storageGroup.enumerateStorages
            ? undefined
            : parseIsoDate(storageGroup.storages[0].startDate),
        endDate: storageGroup.enumerateStorages
            ? undefined
            : parseIsoDate(storageGroup.storages[0].endDate),
        quantity: storageGroup.enumerateStorages
            ? storageGroup.storages.length // storageGroup.quantity only counts active storages, so can't be used here
            : storageGroup.quantity,
        storages: storageGroup.storages.map(storage => ({
            id: storage.id,
            title: storage.title,
            startDate: parseIsoDate(storage.startDate),
            endDate: parseIsoDate(storage.endDate),
            status: storage.status
        })),
        allowCustomArea: storageGroup.rentalType === rentalTypes.customArea.key || (storageGroupCategories[storageGroup.category].allowCustomArea && !getCustomAreaExists(storageSite.storageGroups)),
        lockConfigurations: storageGroup.lockConfigurations,
        storagesAreRectangular: storageGroup.storageLength > 0 && storageGroup.storageWidth > 0,
        showDeletedStorages: false
    };
};

const createFormDataForEmptyTimePeriodPrice = (actor, commissionRate, appContext, populateInitialValue = true) => ({
    amount: undefined,
    eligibleForVat: actor?.authenticationType === organizationTypes.business.key,
    vatRate: undefined,
    commissionRate: formatNumber(100 * commissionRate, appContext),
    startDate: undefined,
    endDate: undefined,
    editable: true,
    endDateEditable: true,
    inUse: false,
    isNew: true,
    initialValue: populateInitialValue
        ? createFormDataForEmptyTimePeriodPrice(actor, commissionRate, appContext, false)
        : undefined
});

const createFormDataForDiscount = (discount, appContext) => ({
    id: discount.id,
    percentage: discount.percentage === undefined
        ? undefined
        : formatNumber(100 * discount.percentage, appContext, formatNumberOptions),
    startDate: parseIsoDate(discount.startDate),
    endDate: parseIsoDate(discount.endDate),
    numberOfMonths: discount.numberOfMonths,
    minimumBookingLength: discount.minimumBookingLength
});

const createFormDataForStorageGroupLockConfigurationDialog = () => ({
    storageGroupIds: [],
    lockConfigurations: []
});

const filterFacilities = (facilities, facilitiesToShow) => {
    return facilities.filter(facility => facilitiesToShow.find(o => o.key === facility));
};

const createStorageGroupsFromFormData = (formValues, actor, appContext) => {
    const optionToTax = getOptionToTaxFromFormData(formValues);
    return getAllStorageGroups(formValues)
        .map(sg => createStorageGroupFromFormData(sg, actor, optionToTax, appContext));
};

const getStorageSiteArea = formValues => {
    return getCustomAreaExists(getAllStorageGroups(formValues))
        ? convertToNumber(formValues.steps[0].area)
        : undefined;
};

const shouldSetQuantity = rentalType => rentalType === rentalTypes.fixedArea.key;

export const createStorageGroupFromFormData = (storageGroupFormValues, actor, optionToTax, appContext) => {
    const category = storageGroupFormValues.category;
    const includeLengthAndWidthMeasurements = storageGroupFormValues.storagesAreRectangular && storageGroupFormValues.rentalType !== rentalTypes.customArea.key;
    const storageGroup = {
        id: storageGroupFormValues.id,
        category,
        title: storageGroupFormValues.title,
        status: storageGroupFormValues.status,
        rentalType: storageGroupFormValues.rentalType,
        storageLength: includeLengthAndWidthMeasurements
            ? convertToNumber(storageGroupFormValues.storageLength)
            : undefined,
        storageWidth: includeLengthAndWidthMeasurements
            ? convertToNumber(storageGroupFormValues.storageWidth)
            : undefined,
        ceilingHeight: convertToNumber(storageGroupFormValues.ceilingHeight),
        minBookableArea: convertToNumber(storageGroupFormValues.minBookableArea),
        timePeriodPrices: storageGroupFormValues.timePeriodPrices.map(tpp => ({
            id: tpp.id,
            amount: convertToNumber(tpp.amount),
            commissionRate: convertToNumber(tpp.commissionRate) / 100,
            startDate: formatIsoDate(tpp.startDate, appContext),
            endDate: formatIsoDate(tpp.endDate, appContext)
        })),
        currency: storageGroupFormValues.currency,
        vatMode: calculateVatMode(category, actor, optionToTax),
        enumerateStorages: storageGroupFormValues.enumerateStorages,
        quantity: shouldSetQuantity(storageGroupFormValues.rentalType)
            ? convertToNumber(storageGroupFormValues.quantity)
            : undefined,
        lockConfigurations: sanitizeStorageGroupLockConfigurations(storageGroupFormValues.lockConfigurations),
        storages: createStorages(storageGroupFormValues, appContext)
    };

    if(storageGroupFormValues.rentalType !== rentalTypes.customArea.key) {
        storageGroup.storageArea = storageGroupFormValues.storagesAreRectangular
            ? convertToNumber(storageGroupFormValues.storageLength) * convertToNumber(storageGroupFormValues.storageWidth)
            : convertToNumber(storageGroupFormValues.storageArea);
    }

    return storageGroup;
};

export const createAdditionalServiceFromFormData = (additionalServiceFormValues, appContext) => ({
    id: additionalServiceFormValues.id,
    description: additionalServiceFormValues.description,
    timePeriodPrices: additionalServiceFormValues.timePeriodPrices.map(tpp => ({
        id: tpp.id,
        amount: convertToNumber(tpp.amount),
        vatRate: tpp.eligibleForVat ? appContext.defaultVatRate : 0,
        commissionRate: convertToNumber(tpp.commissionRate) / 100,
        startDate: formatIsoDate(tpp.startDate, appContext),
        endDate: formatIsoDate(tpp.endDate, appContext)
    })),
    currency: additionalServiceFormValues.currency,
    paymentRecurrence: additionalServiceFormValues.paymentRecurrence,
    type: additionalServiceFormValues.type
});

const createStorages = (storageGroupFormValues, appContext) => {
    if (shouldSetQuantity(storageGroupFormValues.rentalType) && storageGroupFormValues.enumerateStorages) {
        return storageGroupFormValues.storages.map(storage => ({
            id: storage.id,
            title: storage.title,
            startDate: formatIsoDate(storage.startDate, appContext),
            endDate: formatIsoDate(storage.endDate, appContext),
            status: storage.status
        }));
    }

    // we're not enumerating; just use one storage
    return [
        {
            id: storageGroupFormValues.storages && storageGroupFormValues.storages.length > 0
                ? storageGroupFormValues.storages[0].id
                : storageGroupFormValues.storageIds[0],
            title: undefined,
            startDate: formatIsoDate(storageGroupFormValues.startDate, appContext),
            endDate: formatIsoDate(storageGroupFormValues.endDate, appContext),
            status: storageStatuses.active.key
        }
    ];
};

const getStorageTypeFromFormData = formValues => {
    const category = storageGroupCategories[getAllStorageGroups(formValues)[0].category];
    return category.defaultStorageType.key;
};

export const getStorageGroupCategoriesFromFormData = (formValues, appContext) => {
    return appContext.storageGroupCategories.filter(o => formValues.steps[0][o.key].storageGroups.length > 0);
};

export const getDiscountFromFormData = (formDataDiscount, appContext) => ({
    id: formDataDiscount.id,
    percentage: convertToNumber(formDataDiscount.percentage) / 100,
    startDate: formatIsoDate(formDataDiscount.startDate, appContext),
    endDate: formatIsoDate(formDataDiscount.endDate, appContext),
    numberOfMonths: convertToNumber(formDataDiscount.numberOfMonths),
    minimumBookingLength: convertToNumber(formDataDiscount.minimumBookingLength)
});

// "frivilligt skattskyldig": only possible for business + (storeHouse || buildingsAndLand)
export const showOptionToTax = (actor, categories) =>
    actor.organizationType === organizationTypes.business.key &&
    categories.filter(category => category === storageGroupCategories.storeHouse.key || category === storageGroupCategories.buildingsAndLand.key).length > 0;

const getOptionToTax = storageSite => storageSite.storageGroups.filter(sg => sg.vatMode === vatModes.businessesOnlyEligibleForVat.key).length > 0;

export const getOptionToTaxFromFormData = formValues => {
    return formValues.steps[0].optionToTax;
};

export const showExclusiveVatSuffix = (actor, categories, optionToTax) =>
    actor.organizationType === organizationTypes.business.key &&
    (categories.filter(category => category === storageGroupCategories.vehicle.key).length > 0 || optionToTax);

const calculateVatMode = (category, actor, optionToTax) => {
    switch (actor.organizationType) {
        case organizationTypes.private.key:
            return vatModes.notEligibleForVat.key;

        case organizationTypes.business.key:
            switch (category) {
                case storageGroupCategories.storeHouse.key:
                case storageGroupCategories.buildingsAndLand.key:
                    return optionToTax
                        ? vatModes.businessesOnlyEligibleForVat.key
                        : vatModes.notEligibleForVat.key;

                case storageGroupCategories.vehicle.key:
                case storageGroupCategories.office.key:
                    return vatModes.eligibleForVat.key;

                default:
                    throw new Error(`Unknown storage group category '${JSON.stringify(category)}'.`);
            }

        default:
            throw new Error(`Unknown organization type '${JSON.stringify(actor.organizationType)}'.`);
    }
};

export const getDefaultCommissionRate = (actor, storageSiteFormData) => convertToNumber(storageSiteFormData.steps[5].commissionRate) !== undefined
    ? convertToNumber(storageSiteFormData.steps[5].commissionRate) / 100
    : actor.commissionRate;

const sanitizeStorageGroupLockConfigurations = lockConfigurations => lockConfigurations.map(o => ({
    ...o,
    name: lockConfigurations.length > 1
        ? o.name
        : undefined
}));

const createDocumentFormData = document => ({
    id: document.id,
    mediaUrl: document.mediaUrl,
    description: document.description,
});

const createFormDataForStorageSiteLockConfigurations = storageSite => {
    const lockConfigurations = {};
    Object.values(lockTypes).forEach(lockType => {
        lockConfigurations[lockType.key] = {};
    });
    if(storageSite.lockType) {
        lockConfigurations[storageSite.lockType] = storageSite.lockConfiguration;
    }
    return lockConfigurations;
};

const createFormDataForStorageSiteLockSystemUsers = storageSite => storageSite.lockSystemUsers
    ?.map(o => ({
        id: o.id,
        name: o.name,
        description: o.description,
        lockSystemUserIdentifier: o.lockSystemUserIdentifier,
        startDate: parseIsoDate(o.startDate),
        endDate: parseIsoDate(o.endDate)
    }));

const createFormDataForStorageSiteContacts = storageSite => {
    const result = {};
    [contactTypes.caretakerEmail.key, contactTypes.caretakerMobileTelephone.key, contactTypes.caretakerName.key, contactTypes.roundReportEmail.key]
        .forEach(type => {
            result[type] = storageSite.contacts.filter(o => o.type === type).map(o => o.value).join(', ');
        });
    return result;
};

export const getStorageSiteLockConfiguration = formData => formData.steps[5].lockType === 'none'
    ? undefined
    : formData.steps[5].lockConfigurations[formData.steps[5].lockType];

const createFormDataForPriceAffectedBookingsDialog = () => ({
    updateMode: storageSiteBookingUpdateModes.noBookings.key,
    sendPriceChangeEmails: false,
    bookings: [],
    priceChangeEmailCustomMessage: null
});

const parseContact = (type, value) =>
    replaceAll(value ?? '', ';', ',')
        .split(',')
        .map(o => o.trim())
        .filter(o => o)
        .map(o => ({
            type,
            value: o
        }));

export const canonicalizeStorageSiteContacts = formValueContactForType => {
    const parsed = parseContact(undefined, formValueContactForType).map(o => o.value);
    return parsed.length === 0
        ? undefined
        : parsed.join(', ');
};
